import type { AxiosError, AxiosRequestConfig } from "axios";

/*
 * Modelo de erro normalizado do HTTP client (CP-C — ADR 0026).
 *
 * Objetivos:
 *  - I4 (sem vazamento em log): NENHUM objeto exposto por este módulo carrega o
 *    header `Authorization` nem qualquer token. O scrub (`redigirAuthorization`)
 *    é aplicado ANTES de qualquer log/report.
 *  - 404 sem distinção IDOR vs. inexistente: ambos colapsam em
 *    `CategoriaErro.NaoEncontrado`, com a MESMA mensagem genérica — a UI nunca
 *    consegue inferir se o recurso existe e pertence a outra empresa, ou se nunca
 *    existiu (anti-enumeração, espelha a borda do backend).
 *  - 503 (flag dormente OFF): `CategoriaErro.ModuloIndisponivel`, sinal tipado
 *    que a UI futura consome SEM disparar refresh.
 */

const REDACTED = "[REDACTED]";

/**
 * Campos de body que carregam token/credencial e NUNCA podem chegar a um log.
 * Cobre as duas convenções de naming que podem aparecer no wire (snake_case do
 * backend) e em código JS (camelCase), por robustez. Inclui `senha` — credencial
 * write-only do CRUD de usuários (POST/PATCH `/usuarios`, FE-4): scrub na origem,
 * latente≠seguro (mesmo padrão de `refresh_token`).
 *
 * Inclui também as credenciais write-only de CANAL de alerta (tela 6, POST/PATCH
 * `/api/canais`): `password` (ConfigSmtp), `webhook_url` (ConfigWebhook
 * Teams/Slack) e `token` (ConfigWhatsApp) — ver `lib/config-alertas/tipos.ts`.
 * Essas chegam ANINHADAS sob `config` no body, não no top-level; por isso o scrub
 * do caminho OBJETO recursa em objetos/arrays (ver `redigirEmProfundidade`).
 */
const CAMPOS_TOKEN_BODY = [
  "refresh_token",
  "access_token",
  "refreshToken",
  "accessToken",
  "senha",
  "password",
  "webhook_url",
  "token",
] as const;

/** Profundidade máxima do scrub recursivo — guarda contra ciclos/aninhamento patológico. */
const PROFUNDIDADE_MAX = 6;

/** Conjunto p/ lookup O(1) de chave sensível no scrub recursivo. */
const CAMPOS_TOKEN_SET: ReadonlySet<string> = new Set(CAMPOS_TOKEN_BODY);

/** Categorias estáveis que a UI consome — sem vazar o motivo real do servidor. */
export enum CategoriaErro {
  /** 401 após refresh esgotado / sessão expirada. */
  SessaoExpirada = "sessao_expirada",
  /** 403 — autenticado, sem permissão. */
  SemPermissao = "sem_permissao",
  /** 404 — recurso inexistente OU IDOR; indistinguível por decisão. */
  NaoEncontrado = "nao_encontrado",
  /** 503 — módulo dormente (flag default-OFF) ou indisponibilidade transitória. */
  ModuloIndisponivel = "modulo_indisponivel",
  /** 4xx de validação / requisição malformada. */
  RequisicaoInvalida = "requisicao_invalida",
  /** 5xx (exceto 503) — falha do servidor. */
  ErroServidor = "erro_servidor",
  /** Falha de transporte (sem resposta): rede, timeout, CORS. */
  Rede = "rede",
  /** Qualquer outro caso não mapeado. */
  Desconhecido = "desconhecido",
}

/** Mensagens genéricas e estáveis — nunca ecoam corpo/motivo do servidor. */
const MENSAGENS: Record<CategoriaErro, string> = {
  [CategoriaErro.SessaoExpirada]: "Sessão expirada. Faça login novamente.",
  [CategoriaErro.SemPermissao]: "Você não tem permissão para esta operação.",
  [CategoriaErro.NaoEncontrado]: "Recurso não encontrado.",
  [CategoriaErro.ModuloIndisponivel]: "Módulo indisponível no momento.",
  [CategoriaErro.RequisicaoInvalida]: "Requisição inválida.",
  [CategoriaErro.ErroServidor]: "Erro no servidor. Tente novamente.",
  [CategoriaErro.Rede]: "Falha de conexão. Verifique sua rede.",
  [CategoriaErro.Desconhecido]: "Ocorreu um erro inesperado.",
};

/**
 * Erro normalizado que sai do HTTP client. Substitui o `AxiosError` cru no
 * caminho de rejeição — não carrega config/headers crus, logo não vaza token.
 */
export class ErroHttp extends Error {
  readonly categoria: CategoriaErro;
  /** Status HTTP quando houve resposta; `null` em falha de transporte. */
  readonly status: number | null;

  constructor(categoria: CategoriaErro, status: number | null) {
    super(MENSAGENS[categoria]);
    this.name = "ErroHttp";
    this.categoria = categoria;
    this.status = status;
  }

  /** Conveniência para a UI: o módulo está dormente/indisponível? */
  get moduloIndisponivel(): boolean {
    return this.categoria === CategoriaErro.ModuloIndisponivel;
  }

  /** Conveniência para a UI: a sessão expirou (refresh esgotado)? */
  get sessaoExpirada(): boolean {
    return this.categoria === CategoriaErro.SessaoExpirada;
  }
}

/** Mapeia o status HTTP (ou ausência de resposta) para a categoria estável. */
export function categoriaPorStatus(status: number | null): CategoriaErro {
  if (status === null) return CategoriaErro.Rede;
  if (status === 401) return CategoriaErro.SessaoExpirada;
  if (status === 403) return CategoriaErro.SemPermissao;
  if (status === 404) return CategoriaErro.NaoEncontrado;
  if (status === 503) return CategoriaErro.ModuloIndisponivel;
  if (status >= 500) return CategoriaErro.ErroServidor;
  if (status >= 400) return CategoriaErro.RequisicaoInvalida;
  return CategoriaErro.Desconhecido;
}

/** Objeto "plain" (não-null, não-array, não-Date/etc.) seguro para recursar. */
function ehObjetoPlano(valor: unknown): valor is Record<string, unknown> {
  return (
    typeof valor === "object" &&
    valor !== null &&
    !Array.isArray(valor) &&
    Object.getPrototypeOf(valor) === Object.prototype
  );
}

/**
 * Redige IN PLACE os campos sensíveis em PROFUNDIDADE: percorre objetos planos e
 * arrays, redigindo o valor de qualquer chave ∈ CAMPOS_TOKEN_BODY e recursando nos
 * containers aninhados (alcança `config.password`, `config.webhook_url`, etc.).
 * Só recursa em objetos plain e arrays — null/Date/primitivos são ignorados. A
 * guarda de profundidade evita aninhamento patológico/ciclos.
 */
function redigirEmProfundidade(valor: unknown, profundidade: number): void {
  if (profundidade > PROFUNDIDADE_MAX) return;

  if (Array.isArray(valor)) {
    for (const item of valor) {
      redigirEmProfundidade(item, profundidade + 1);
    }
    return;
  }

  if (!ehObjetoPlano(valor)) return;

  for (const chave of Object.keys(valor)) {
    if (CAMPOS_TOKEN_SET.has(chave)) {
      valor[chave] = REDACTED;
    } else {
      redigirEmProfundidade(valor[chave], profundidade + 1);
    }
  }
}

/**
 * Redige tokens dentro do BODY da request (`config.data`), IN PLACE (I4).
 *
 * O body do POST /api/auth/refresh carrega `{ refresh_token }` em claro — se o
 * `AxiosError` cru chegar a um log, o header redigido NÃO basta: o token também
 * está no corpo. Cobre as DUAS formas que o `config.data` pode assumir:
 *
 *  - OBJETO (`{ refresh_token: "..." }`): redige o valor de cada campo sensível.
 *  - STRING JSON (axios pode já ter aplicado `transformRequest`, virando
 *    `'{"refresh_token":"..."}'`): tenta `JSON.parse` seguro e re-serializa com
 *    os campos redigidos; se o parse falhar, cai num regex que zera só o VALOR
 *    do campo sensível, preservando o resto da string.
 *
 * Tolerante a `config`/`data` ausentes; não lança.
 */
function redigirBody(config: AxiosRequestConfig | undefined): void {
  const data = config?.data;
  if (data === null || data === undefined) return;

  if (typeof data === "object") {
    // Recursa em objetos/arrays aninhados — as credenciais de canal vivem sob
    // `config` (ex.: `config.password`), não no top-level do body.
    redigirEmProfundidade(data, 0);
    return;
  }

  if (typeof data === "string" && config) {
    config.data = redigirBodyString(data);
  }
}

/**
 * Redige campos de token numa STRING (JSON serializado). Tenta parse estruturado
 * primeiro; em fallback (não-JSON), aplica regex no VALOR de cada campo sensível.
 */
function redigirBodyString(texto: string): string {
  try {
    const parsed: unknown = JSON.parse(texto);
    if (parsed !== null && typeof parsed === "object") {
      // Recursa em profundidade (alcança credenciais de canal aninhadas em
      // `config`), depois re-serializa a estrutura redigida.
      redigirEmProfundidade(parsed, 0);
      return JSON.stringify(parsed);
    }
  } catch {
    // Não é JSON parseável — cai no regex abaixo.
  }

  // Fallback regex: substitui só o VALOR (entre aspas) de cada campo sensível.
  let resultado = texto;
  for (const campo of CAMPOS_TOKEN_BODY) {
    const padrao = new RegExp(`("${campo}"\\s*:\\s*)"[^"]*"`, "g");
    resultado = resultado.replace(padrao, `$1"${REDACTED}"`);
  }
  return resultado;
}

/**
 * Redige o header `Authorization` E o body com tokens (I4) em qualquer config de
 * request, IN PLACE. O header cobre `Authorization`/`authorization`; o body cobre
 * `config.data` como objeto OU string JSON. Devolve a mesma referência por
 * conveniência. Tolerante a `config`/`headers`/`data` ausentes.
 */
export function redigirAuthorization(
  config: AxiosRequestConfig | undefined,
): AxiosRequestConfig | undefined {
  const headers = config?.headers as Record<string, unknown> | undefined;
  if (headers) {
    for (const chave of Object.keys(headers)) {
      if (chave.toLowerCase() === "authorization") {
        headers[chave] = REDACTED;
      }
    }
  }
  redigirBody(config);
  return config;
}

/**
 * Converte um `AxiosError` em `ErroHttp`, REDIGINDO o `Authorization` do
 * `error.config` antes de devolver. O `ErroHttp` resultante é seguro para log:
 * não referencia o config/headers crus. Chame este conversor no ÚLTIMO ponto
 * antes de propagar a rejeição ao chamador.
 */
export function normalizarErroAxios(erro: AxiosError): ErroHttp {
  // Scrub IN PLACE no objeto cru, caso algum log já tenha referência a `erro`.
  redigirAuthorization(erro.config);
  const status = erro.response?.status ?? null;
  return new ErroHttp(categoriaPorStatus(status), status);
}
